/**
 * Client Members Hook
 *
 * DEPRECATED: Legacy Supabase implementation
 * Phase 6 (2026-02-11): Azure Migration Complete - This hook is no longer used
 * TODO: Remove this file after confirming no dependencies
 *
 * Direct Supabase queries for managing organization members.
 * Phase 3: Role management and member removal
 * - Any organization member can view other members
 * - Only OWNER/ADMIN can change roles or remove members
 */

import { useState, useEffect, useCallback } from 'react';
// DEPRECATED: Supabase removed in Phase 6 Azure migration
// import { getSupabaseClient } from '../../lib/supabase';
import { usePermissions } from '../usePermissions';

/**
 * Map Supabase errors to user-friendly messages
 */
const mapErrorToMessage = (error) => {
  if (!error) return 'Unknown error occurred';

  const message = error.message || String(error);

  // Permission errors
  if (message.includes('permission denied') || message.includes('RLS')) {
    return 'Access denied: You do not have permission to perform this action';
  }

  // Foreign key violations
  if (message.includes('foreign key')) {
    return 'Invalid member reference';
  }

  // Not found errors
  if (message.includes('No rows found') || error.code === 'PGRST116') {
    return 'Member not found';
  }

  // Network errors
  if (message.includes('Failed to fetch') || message.includes('network')) {
    return 'Network error: Please check your connection';
  }

  // Return original message if no match
  return message;
};

export const useClientMembers = (organizationId) => {
  const { currentUser } = usePermissions();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Permission check: User must be a member of the organization
  const canView = useCallback(async () => {
    if (!organizationId || !currentUser?.id) return false;

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return false;

      const { data } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', organizationId)
        .eq('user_id', currentUser.id)
        .single();

      // Any member can view (OWNER/ADMIN/SKIPPER/CREW)
      return data !== null;
    } catch (err) {
      console.error('Permission check error:', err);
      return false;
    }
  }, [organizationId, currentUser]);

  // Fetch members for organization
  const fetchMembers = useCallback(async () => {
    const hasPermission = await canView();
    if (!hasPermission) {
      setError('Access denied: Not a member of this organization');
      setIsLoading(false);
      return;
    }

    if (!organizationId) {
      setMembers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      // Query organization_members
      const { data, error: queryError } = await supabase
        .from('organization_members')
        .select('id, user_id, role, created_at')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      // Phase 2: Skip user enrichment (profiles table doesn't exist yet)
      // Just return members with user_id - Phase 3 will add profile enrichment
      setMembers(data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, canView]);

  // Fetch on org change
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Permission check for mutations: Only OWNER/ADMIN can update roles or remove members
  const canMutate = useCallback(async () => {
    if (!organizationId || !currentUser?.id) return false;

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return false;

      const { data } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', organizationId)
        .eq('user_id', currentUser.id)
        .single();

      // Only OWNER/ADMIN can mutate
      return data && ['OWNER', 'ADMIN'].includes(data.role);
    } catch (err) {
      console.error('Permission check error:', err);
      return false;
    }
  }, [organizationId, currentUser]);

  // Update member role
  const updateMemberRole = useCallback(async (memberId, newRole) => {
    const hasPerm = await canMutate();
    if (!hasPerm) {
      const errorMsg = 'Access denied: OWNER or ADMIN role required';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      const { error: updateError } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId)
        .eq('organization_id', organizationId); // Security: ensure same org

      if (updateError) throw updateError;

      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      console.error('Error updating member role:', err);
      const errorMsg = mapErrorToMessage(err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [canMutate, organizationId, fetchMembers]);

  // Remove member from organization
  const removeMember = useCallback(async (memberId) => {
    const hasPerm = await canMutate();
    if (!hasPerm) {
      const errorMsg = 'Access denied: OWNER or ADMIN role required';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      const { error: deleteError } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId)
        .eq('organization_id', organizationId); // Security: ensure same org

      if (deleteError) throw deleteError;

      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      console.error('Error removing member:', err);
      const errorMsg = mapErrorToMessage(err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [canMutate, organizationId, fetchMembers]);

  return {
    members,
    isLoading,
    error,
    updateMemberRole,
    removeMember,
    refresh: fetchMembers
  };
};

export default useClientMembers;
